import {AfterViewInit, Component, ElementRef, HostListener, Input, ViewChild, ViewContainerRef} from '@angular/core';
import {AuthService} from '../../auth/auth.service';
import {ConfigService} from '../../core/services/config/config.service';
import {DemoResetResult, DemoService} from '../../core/services/demo/demo.service';
import {NotificationPopupService} from '../../shared/notification-popup/services/notification-popup.service';
import {Align} from '@progress/kendo-angular-popup';
import {DialogCloseResult, DialogRef, DialogService} from '@progress/kendo-angular-dialog';

@Component({
    selector: 'app-user-menu',
    templateUrl: './user-menu.component.html',
    styleUrls: ['./user-menu.component.scss']
})
export class UserMenuComponent implements AfterViewInit {
    @ViewChild('popup', {read: ElementRef}) private popup: ElementRef;
    @Input() hideCustomerMenu = false;
    public show = false;
    public anchorAlign: Align = {horizontal: 'right', vertical: 'bottom'};
    public popupAlign: Align = {horizontal: 'left', vertical: 'bottom'};
    public userInitials = '';
    public profilePicUrl = '';
    // Demo tenants simulate re-opt / apply / send in-process; only they get Reset demo
    public demoSimulation = false;
    public resetting = false;

    constructor(public elRef: ElementRef, private authService: AuthService,
                private configService: ConfigService, private demoService: DemoService,
                private dialogService: DialogService, private viewContainerRef: ViewContainerRef,
                private notificationSvc: NotificationPopupService) {
        this.userInitials = this.authService.getUserInitials();
        this.profilePicUrl = this.authService.getProfilePic();
        this.demoSimulation = this.demoService.isDemoSimulation();
    }

    ngAfterViewInit(): void {
        try { 
            // @ts-ignore
            window.zE('webWidget', 'hide');
            // @ts-ignore
            window.zESettings = {
                webWidget: {
                    offset: {horizontal: '90px', vertical: '20px'}
                }
            };
            // @ts-ignore
            window.zE('webWidget:on', 'close', () => {
                // @ts-ignore
                window.zE('webWidget', 'hide');
            });
        } catch (err) {
            console.log('Broswer does not support Zendesk plugin');
        }
    }

    public isScreenEnabled(screenId: string): boolean {
        return this.configService.isScreenEnabled(screenId);
    }

    public logout(): void {
        this.authService.logout();
    }

    public onToggle(show?: boolean): void {
        this.show = show !== undefined ? show : !this.show;
    }

    /** Confirm, then undo every simulated re-opt, rule apply and send on this demo tenant. */
    public resetDemo(): void {
        if (this.resetting) {
            return;
        }
        const dialog: DialogRef = this.dialogService.open({
            title: 'Reset demo',
            content: 'Reset the demo data? This undoes every simulated re-optimization, rule apply and ' +
                'send, and clears pending overrides. Saved business rules and inventory overrides are kept.',
            actions: [
                {text: 'Cancel'},
                {text: 'Reset', primary: true}
            ]
        });
        dialog.result.subscribe(resp => {
            if (resp instanceof DialogCloseResult || resp.text !== 'Reset') {
                return;
            }
            this.runReset();
        });
    }

    private runReset(): void {
        this.resetting = true;
        this.demoService.resetDemo().subscribe((result: DemoResetResult) => {
            this.resetting = false;
            this.notificationSvc.displayNotification(this.viewContainerRef, {
                notificationType: 'success',
                message: `Demo reset: ${result.journeysRestored} journeys restored, ` +
                    `${result.reoptsRemoved} re-opts, ${result.appliesRemoved} applies and ` +
                    `${result.publishesRemoved} sends removed, ` +
                    `${result.overridesRestored} override alerts restored, ` +
                    `${result.priorityOverridesRemoved} rule priority pins removed.`
            });
            this.reload();
        }, error => {
            this.resetting = false;
            console.error(error);
            this.notificationSvc.displayNotification(this.viewContainerRef, {
                notificationType: 'error',
                message: error && error.error && error.error.error
                    ? error.error.error
                    : 'Could not reset the demo. Please try again.'
            });
        });
    }

    // Seam so the reset can be tested without reloading the test runner.
    protected reload(): void {
        window.location.reload();
    }

    public showSupport(): void {
        try {
            // @ts-ignore
            window.zE('webWidget', 'show');
            // @ts-ignore
            window.zE('webWidget', 'open');
        } catch (err) {
            console.log('Broswer does not support Zendesk plugin');
        }
    }

    public userMenuItemClicked(event: any): void {
        if (event.index === '0') {
            this.logout();
        } else if (event.index === '1') {
            try {
                // @ts-ignore
                window.zE('webWidget', 'show');
                // @ts-ignore
                window.zE('webWidget', 'open');
            } catch (err) {
                console.log('Broswer does not support Zendesk plugin');
            }
        }
    }

    @HostListener('document:click', ['$event'])
    private documentClick(event: any): void {
        if (!this.popupContains(event.target)) {
            this.onToggle(false);
        }
    }

    private popupContains(target: any): boolean {
        return this.elRef.nativeElement.parentElement.contains(target) ||
            (this.popup ? this.popup.nativeElement.contains(target) : false);
    }
}
